// Single source of truth for AEN's supported locales.
//
// Before this registry, the same 14-language list was hand-maintained in
// six places — src/i18n.js, src/components/SEO.js (HREFLANG + knowsLanguage),
// src/components/Navbar.js, scripts/generate-sitemap.mjs, public/index.html.
// Add a new locale and one of those drifts out of sync until validate-build
// catches it (or worse, until it ships). Now everything imports from here.
//
// Why .mjs: this file is read by both webpack (CRA src/ imports) and Node
// (scripts/*.mjs at build time). webpack 5 handles `.mjs` natively. Node
// requires the explicit extension because the project's package.json
// doesn't set "type": "module".
//
// public/index.html still inlines the language-code list because it runs
// BEFORE webpack is involved (pre-React language detection on first paint).
// scripts/validate-build.mjs cross-checks that inline list against this
// registry on every build, so drift is caught immediately.

/**
 * @typedef {object} Locale
 * @property {string} code   — ISO 639-1 / BCP-47 base tag
 * @property {string} label  — short uppercase code shown in the switcher
 * @property {string} name   — language endonym (the language naming itself)
 * @property {string} flag   — country flag emoji (best representative, not strict)
 * @property {'ltr'|'rtl'} dir — writing direction for <html dir>
 */

/** Ordered list of every supported language. The order here drives the
 *  Navbar language picker — keep English first, then descending by
 *  approximate audience size for AEN's target market. */
export const LOCALES = [
  { code: 'en', label: 'EN', name: 'English',    flag: '🇬🇧', dir: 'ltr' },
  { code: 'es', label: 'ES', name: 'Español',    flag: '🇪🇸', dir: 'ltr' },
  { code: 'fr', label: 'FR', name: 'Français',   flag: '🇫🇷', dir: 'ltr' },
  { code: 'pl', label: 'PL', name: 'Polski',     flag: '🇵🇱', dir: 'ltr' },
  { code: 'bg', label: 'BG', name: 'Български',  flag: '🇧🇬', dir: 'ltr' },
  { code: 'ru', label: 'RU', name: 'Русский',    flag: '🇷🇺', dir: 'ltr' },
  { code: 'ro', label: 'RO', name: 'Română',     flag: '🇷🇴', dir: 'ltr' },
  { code: 'it', label: 'IT', name: 'Italiano',   flag: '🇮🇹', dir: 'ltr' },
  { code: 'pt', label: 'PT', name: 'Português',  flag: '🇵🇹', dir: 'ltr' },
  { code: 'de', label: 'DE', name: 'Deutsch',    flag: '🇩🇪', dir: 'ltr' },
  { code: 'tr', label: 'TR', name: 'Türkçe',     flag: '🇹🇷', dir: 'ltr' },
  { code: 'ar', label: 'AR', name: 'العربية',   flag: '🇸🇦', dir: 'rtl' },
  { code: 'hi', label: 'HI', name: 'हिन्दी',    flag: '🇮🇳', dir: 'ltr' },
  { code: 'zh', label: 'ZH', name: '中文',       flag: '🇨🇳', dir: 'ltr' },
];

/** Codes only, in registry order. Useful for hreflang lists, sitemap
 *  alternates, i18next supportedLngs, schema.org knowsLanguage, etc. */
export const LOCALE_CODES = LOCALES.map((l) => l.code);

/** Codes that need <html dir="rtl">. Only Arabic at the moment. Add new
 *  RTL languages here (Hebrew, Farsi, Urdu) and every consumer picks them
 *  up automatically. */
export const RTL_LOCALE_CODES = LOCALES.filter((l) => l.dir === 'rtl').map((l) => l.code);

/** Default locale. Drives the canonical URL (no ?lng=) and the x-default
 *  hreflang. Changing this is a major SEO event — update with care. */
export const DEFAULT_LOCALE = 'en';
